import type { CSSProperties, MouseEvent, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useInfoTooltip } from '@/components/readiness/useInfoTooltip'
import { trackReadiness } from '@/lib/readiness/profile'
import type { GateId } from '@/stores/profileStore'

// ── Locked item (Iteration 6 POC §2.1, Note 3) ──────────────────────────────
// One treatment for every control that stays visible but is locked in POC:
// icon and label at 40% opacity (chips and counts at full), a 14px lock where
// the trailing affordance was, no hover, cursor default, `aria-disabled`,
// focusable, inert, with the informational tooltip. Tabs are the exception:
// shown at full opacity with a leading lock, and they still navigate because
// the destination renders its locked preview (§5).

const TIP = 'Available in the full version.'

export function LockIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  )
}

interface LockedItemProps {
  feature: GateId
  variant: 'tab' | 'button' | 'row' | 'drawerItem'
  // Icon and label: dimmed, except on a tab.
  children: ReactNode
  // Chip or count: always full opacity.
  chip?: ReactNode
  // Tabs (and navigable drawer items) only: where they go.
  to?: string
  onNavigate?: () => void
  className?: string
  style?: CSSProperties
  ariaLabel?: string
  testId?: string
  // Extra props for a tab link (role, aria-selected, …).
  linkProps?: Record<string, unknown>
}

export function LockedItem({ feature, variant, children, chip, to, onNavigate, className = '', style, ariaLabel, testId, linkProps }: LockedItemProps) {
  const tip = useInfoTooltip<HTMLElement>({ content: <p>{TIP}</p>, width: 220, testId: `locked-tooltip-${feature}` })
  const trigger = tip.triggerProps as object
  const navigates = !!to && (variant === 'tab' || variant === 'drawerItem')

  if (navigates) {
    return (
      <>
        <Link to={to!} {...linkProps} {...trigger} onClick={onNavigate} className={className} style={style} aria-label={ariaLabel} data-testid={testId} data-locked="true">
          {variant === 'tab' && <LockIcon />}
          {children}
          {variant === 'tab' ? chip : (
            // One trailing cell: the count, then the lock.
            <span className="qz-nav-text inline-flex items-center gap-2">
              {chip}
              <span className="inline-flex" style={{ color: 'var(--fg-3)' }}><LockIcon /></span>
            </span>
          )}
        </Link>
        {tip.node}
      </>
    )
  }

  const onClick = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    trackReadiness({ name: 'readiness.locked_item_activated', feature })
  }
  const base = 'rd-locked'
  if (variant === 'button') {
    // The control's own style (primary / secondary) at 40%, lock before the label.
    return (
      <>
        <button type="button" aria-disabled="true" {...trigger} onClick={onClick} className={`${base} ${className}`} style={{ opacity: 0.4, cursor: 'default', ...style }} aria-label={ariaLabel} data-testid={testId}>
          <LockIcon />
          {children}
          {chip}
        </button>
        {tip.node}
      </>
    )
  }
  // row / inert drawerItem: content dimmed, chip full, lock trailing.
  return (
    <>
      <button type="button" aria-disabled="true" {...trigger} onClick={onClick} className={`${base} ${className}`} style={{ cursor: 'default', ...style }} aria-label={ariaLabel} data-testid={testId}>
        <span className="flex min-w-0 flex-1 items-center gap-2.5" style={{ opacity: 0.4 }}>{children}</span>
        {chip}
        <span className="inline-flex shrink-0" style={{ color: 'var(--rd-text-muted)' }}><LockIcon /></span>
      </button>
      {tip.node}
    </>
  )
}
